import { isNullOrUndefined } from 'ContentUtils/helpers/ObjectHelpers';
import { BREAKPOINTS } from 'EmailEditorUI/constants/MobileEditingConstants';
import { STACK } from 'EmailEditorUI/constants/SectionLayoutConstants';
function getInBreakpointStyles(styles, path) {
  return styles.getIn(['breakpointStyles', ...path]);
}
export function getDefaultStyle(styles, name) {
  return getInBreakpointStyles(styles, ['default', name]);
}
export function getMobileStyle(styles, name) {
  return getInBreakpointStyles(styles, ['mobile', name]);
}
export function isHiddenForAllBreakpoints(styles) {
  const breakpointStyles = styles === null || styles === void 0 ? void 0 : styles.get('breakpointStyles');
  if (!breakpointStyles) {
    return false;
  }
  const mobileHidden = getMobileStyle(styles, 'hidden');
  const defaultHidden = getDefaultStyle(styles, 'hidden');
  if (isNullOrUndefined(mobileHidden)) {
    return Boolean(defaultHidden);
  }
  return Boolean(mobileHidden && defaultHidden);
}
export function isVisibleForAllBreakpoints(styles) {
  const breakpointStyles = styles === null || styles === void 0 ? void 0 : styles.get('breakpointStyles');
  if (!breakpointStyles) {
    return true;
  }
  const mobileHidden = getMobileStyle(styles, 'hidden');
  const defaultHidden = getDefaultStyle(styles, 'hidden');
  if (isNullOrUndefined(mobileHidden)) {
    return !defaultHidden;
  }
  return !mobileHidden && !defaultHidden;
}
export function isHiddenForDefaultBreakpoint(styles) {
  if (!styles) {
    return false;
  }
  return Boolean(getDefaultStyle(styles, 'hidden'));
}
export function isHiddenForMobileBreakpoint(styles) {
  if (!styles) {
    return false;
  }
  const mobileHidden = getMobileStyle(styles, 'hidden');
  const defaultHidden = getDefaultStyle(styles, 'hidden');
  if (isNullOrUndefined(mobileHidden)) {
    return Boolean(defaultHidden);
  }
  return Boolean(mobileHidden);
}
export function isHiddenForSomeBreakpoint(styles) {
  return isHiddenForDefaultBreakpoint(styles) || isHiddenForMobileBreakpoint(styles);
}
export function isHiddenForBreakpoint(breakpoint, styles) {
  if (breakpoint === 'mobile') {
    return isHiddenForMobileBreakpoint(styles);
  }
  return isHiddenForDefaultBreakpoint(styles);
}
export function normalizeBreakpointStylesInternal({
  breakpointName,
  defaultStyles = {},
  isEditingForAllDevices,
  newStyles,
  styles
}) {
  Object.entries(newStyles).forEach(([property, value]) => {
    if (breakpointName === 'default') {
      var _styles$get;
      const defaultValue = (_styles$get = styles.get(property)) !== null && _styles$get !== void 0 ? _styles$get : getDefaultStyle(styles, property);
      const mobileValue = getMobileStyle(styles, property);

      /*
       * If changing the desktop value while "all devices" is off, we want to set
       * the mobile style to have the currently inherited desktop style, so that
       * desktop changes do not produce any changes to the mobile design.
       */
      if (isNullOrUndefined(mobileValue) && !isEditingForAllDevices) {
        const currentInheritedStyle = defaultValue !== null && defaultValue !== void 0 ? defaultValue : defaultStyles[property];
        styles = styles.setIn(['breakpointStyles', 'mobile', property], currentInheritedStyle);
      }
      if (isEditingForAllDevices) {
        styles = styles.setIn(['breakpointStyles', 'mobile', property], null);
      }
    }
    styles = styles.setIn(['breakpointStyles', breakpointName, property], value);
  });
  return styles;
}
export function normalizeElementBreakpointStyles({
  breakpointName,
  isEditingForAllDevices,
  newStyles,
  styles
}) {
  return normalizeBreakpointStylesInternal({
    breakpointName,
    isEditingForAllDevices,
    newStyles,
    styles,
    defaultStyles: {
      hidden: false
    }
  });
}
export function normalizeSectionBreakpointStyles({
  breakpointName,
  isEditingForAllDevices,
  newStyles,
  styles
}) {
  return normalizeBreakpointStylesInternal({
    breakpointName,
    isEditingForAllDevices,
    newStyles,
    styles,
    defaultStyles: {
      backgroundImage: 'none',
      backgroundType: 'CONTENT',
      borderTop: {
        type: 'NONE',
        width: '0px',
        color: '#000000'
      },
      borderRight: {
        type: 'NONE',
        width: '0px',
        color: '#000000'
      },
      borderBottom: {
        type: 'NONE',
        width: '0px',
        color: '#000000'
      },
      borderLeft: {
        type: 'NONE',
        width: '0px',
        color: '#000000'
      },
      borderTopLeftRadius: '0px',
      borderTopRightRadius: '0px',
      borderBottomLeftRadius: '0px',
      borderBottomRightRadius: '0px',
      hidden: false,
      marginBottom: '0px',
      marginTop: '0px',
      paddingBottom: '0px',
      paddingTop: '0px'
    }
  });
}
export function getBreakpointsWithHiddenElements(modulesOrSections) {
  return Object.values(BREAKPOINTS).reduce((acc, breakpoint) => {
    const hasHidden = !!modulesOrSections.find(item => isHiddenForBreakpoint(breakpoint, item === null || item === void 0 ? void 0 : item.get('style')) || isHiddenForBreakpoint(breakpoint, item === null || item === void 0 ? void 0 : item.get('styles')));
    if (hasHidden) {
      acc.push(breakpoint);
    }
    return acc;
  }, new Array());
}
export function getStackingTypesUsed(sections) {
  return Array.from(sections.reduce((acc, current) => {
    acc.add(current.getIn(['style', 'stack']) || STACK.LEFT_TO_RIGHT);
    return acc;
  }, new Set()));
}